import type { RestrictionsByType } from '../../../types/columbia';
import type { IModularBenefitContact } from '../../../types/modular-benefit';
import { AcceptedContact } from '@ben/shared/utils/contacts';
import { useMemo } from 'react';

interface UseContactSelectOptionsProps {
  contacts: IModularBenefitContact[];
  selectedContactIds: string[];
  restrictionsByType: RestrictionsByType;
}

const useContactSelectOptions = ({
  contacts,
  selectedContactIds,
  restrictionsByType,
}: UseContactSelectOptionsProps) => {
  const maxSelectionsByContactType = useMemo(
    () => ({
      [AcceptedContact.SPOUSE]: restrictionsByType?.spouse?.maxSelections,
      [AcceptedContact.CHILD]: restrictionsByType?.child?.maxSelections,
      [AcceptedContact.FRIEND]: restrictionsByType?.friend?.maxSelections,
      [AcceptedContact.OTHER]: restrictionsByType?.other?.maxSelections,
    }),
    [restrictionsByType]
  );

  const { selectedCountByContactType, availableContacts } = useMemo(() => {
    const selectedContactIdSet = new Set(selectedContactIds);
    const selectedCountByContactType: Record<string, number> = {};
    const availableContacts: IModularBenefitContact[] = [];

    for (const contact of contacts) {
      const isSelected = selectedContactIdSet.has(String(contact.id));

      if (isSelected) {
        selectedCountByContactType[contact.contactTypeKey] =
          (selectedCountByContactType[contact.contactTypeKey] || 0) + 1;
      } else if (
        !contact.disabled ||
        (contact.disabled && contact.disabledReasons?.length > 0)
      ) {
        availableContacts.push(contact);
      }
    }

    return { selectedCountByContactType, availableContacts };
  }, [contacts, selectedContactIds]);

  const contactSelectOptions = useMemo(
    () =>
      availableContacts.map((contact) => {
        const contactTypeKey = contact.contactTypeKey;
        const maxSelections = (
          maxSelectionsByContactType as Record<string, number | undefined>
        )[contactTypeKey];

        const hasReachedMaxSelectionsForType =
          typeof maxSelections === 'number' &&
          (selectedCountByContactType[contactTypeKey] || 0) >= maxSelections;

        const isDisabled = hasReachedMaxSelectionsForType || contact.disabled;

        return {
          label: `${contact.firstName} ${contact.lastName} ${
            contact.disabled ? `(${contact.disabledReasons?.join(', ')})` : ''
          }`,
          value: String(contact.id),
          disabled: isDisabled,
        };
      }),
    [availableContacts, maxSelectionsByContactType, selectedCountByContactType]
  );

  const noSelectableContacts =
    contactSelectOptions.filter((contact) => !contact.disabled).length === 0;

  return {
    contactSelectOptions,
    noSelectableContacts,
  };
};

export default useContactSelectOptions;
